import { get, toString } from "lodash";
import { useMemo, useRef } from "react";
import { BsFilter } from "react-icons/bs";
import { MdFilterAlt } from "react-icons/md";
import { BiSortUp, BiSortDown } from "react-icons/bi";
import { parseColumnId } from "../ReactGridCustom/utils";
import { Styles } from "./styled";
import {
  FILTER_ANCHOR_ATTR,
  computeFilterPanelPositionFromRect,
} from "./filterPanelAnchor";
import { DisplaySize } from "@lhd/constants";

export interface Position {
  x: number;
  y: number;
}

export interface IFilterInfo {
  open?: boolean;
  columnId: string | number | null;
  position?: Position;
  filterOrder?: string[];
}
interface FilterPanelProps {
  isActive?: boolean;
  className?: string;
  disabled?: boolean;
  filterInfo?: IFilterInfo;
  setFilterInfo?: (filterInfo: IFilterInfo) => void;
  columnId?: string;
  showFilterIcon: boolean;
  filter?: any;
  additionalField?: string[];
  skipParseColumnId?: boolean;
  displaySize?: DisplaySize;
}

const FilterIcon = (props: FilterPanelProps) => {
  const {
    setFilterInfo,
    className,
    columnId,
    disabled,
    isActive,
    showFilterIcon,
    filter,
    additionalField,
    skipParseColumnId,
    displaySize,
  } = props;
  const filterRef = useRef<HTMLDivElement>(null);

  const openFilterPanel = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (disabled) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const target = e.currentTarget;
    const newPosition = computeFilterPanelPositionFromRect(
      target.getBoundingClientRect(),
    );

    setFilterInfo?.({
      ...(props.filterInfo || {}),
      open: true,
      columnId: columnId || "",
      position: newPosition,
    });
  };
  const strColumnId = toString(columnId);
  const parsedColumnId = skipParseColumnId
    ? strColumnId
    : parseColumnId(strColumnId, additionalField);

  let filterValue = get(filter, parsedColumnId);
  if (!filterValue && parsedColumnId !== strColumnId) {
    filterValue = get(filter, strColumnId);
  }
  const isSearchActive = Boolean(
    filterValue &&
      typeof filterValue === "object" &&
      (typeof filterValue.search === "string"
        ? filterValue.search.trim() !== ""
        : filterValue.search),
  );

  let hasCheckboxFilter = false;
  if (
    filterValue &&
    typeof filterValue === "object" &&
    !Array.isArray(filterValue) &&
    "filter" in filterValue &&
    filterValue.filter !== undefined
  ) {
    hasCheckboxFilter = true;
  }
  const hasBlankFilter = Boolean(
    filterValue &&
      typeof filterValue === "object" &&
      !Array.isArray(filterValue) &&
      filterValue.filter_special_empty,
  );
  const isFilterAction = isSearchActive || hasCheckboxFilter || hasBlankFilter;
  const showFilterIconState = Boolean(isActive) || isFilterAction;
  const hasSort =
    typeof filterValue === "object" &&
    filterValue !== null &&
    !Array.isArray(filterValue) &&
    (filterValue?.sort === "asc" || filterValue?.sort === "desc");

  const sizes = useMemo(() => {
    switch (displaySize) {
      case "small":
        return {
          bsFilter: 14,
          imArrowUp: 11,
          imArrowDown: 11,
          mdFilterAlt: 22,
          sizeContainer: 13,
        };
      case "medium":
        return {
          bsFilter: 17,
          imArrowUp: 13,
          imArrowDown: 13,
          mdFilterAlt: 22,
          sizeContainer: 20,
        };
      default:
        return {
          bsFilter: 19,
          imArrowUp: 15,
          imArrowDown: 15,
          mdFilterAlt: 22,
          sizeContainer: 22,
        };
    }
  }, [displaySize]);
  const renderIconFilter = () => {
    if (showFilterIconState) {
      return <MdFilterAlt size={sizes.mdFilterAlt} color="#ffffff" />;
    }
    if (hasSort) {
      return filterValue?.sort === "asc" ? (
        <BiSortUp size={22} color="#ffffff" />
      ) : (
        <BiSortDown size={22} color="#ffffff" />
      );
    }
    return <BsFilter size={sizes.bsFilter} color="#ffffff" />;
  };

  return showFilterIcon ? (
    <Styles
      onPointerDown={(e) => {
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      className="icon_filter_container"
    >
      <div
        ref={filterRef}
        {...{ [FILTER_ANCHOR_ATTR]: String(columnId ?? "") }}
        style={{
          height: sizes.sizeContainer,
          width: sizes.sizeContainer,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        className={`header-icon-filter${disabled ? " disabled" : ""}${
          className ? ` ${className}` : ""
        }`}
        aria-disabled={disabled}
        onPointerDown={openFilterPanel}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {renderIconFilter()}
      </div>
    </Styles>
  ) : null;
};
export default FilterIcon;
